/**
 * FunChessboard
 *
 * Dual-target chessboard for the Fun Challenges feature. Web renders via
 * `react-chessboard` directly (lightweight, no piece images, click + highlight
 * only); native falls back to a 64-square `Pressable` grid.
 *
 * The standalone Square Trainer at
 * src/Screens/Visualisation/components/SquareTrainer/index.tsx uses
 * `chessboard-package` (the heavier CircleChess wrapper) plus the
 * `BlindFoldChess` shell. We deliberately do NOT route through that here —
 * the wrapper owns chess-play state (lastMove, kingInCheckSquare,
 * possibleMoves, drag) we don't need for square trainer. The trade-off is
 * intentional and matches the nscl battle adapter pattern.
 */

import React, {useMemo} from 'react';
import {Platform, Pressable, StyleSheet, Text, View} from 'react-native';

const LIGHT_SQUARE = '#EDD899';
const DARK_SQUARE  = '#D1AB41';
const NOTATION_COLOR = 'rgba(38,35,34,0.5)';

const FILES = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
const RANKS = ['1', '2', '3', '4', '5', '6', '7', '8'];

function getSquareName(
  fileIndex: number,
  rankIndex: number,
  orientation: 'white' | 'black',
): string {
  if (orientation === 'white') {
    return FILES[fileIndex] + RANKS[7 - rankIndex];
  }
  return FILES[7 - fileIndex] + RANKS[rankIndex];
}

interface NativeFallbackBoardProps {
  boardWidth: number;
  orientation: 'white' | 'black';
  highlightedSquare: string | null;
  highlightColor: string;
  onSquareClick: (square: string) => void;
  disabled: boolean;
}

function NativeFallbackBoard({
  boardWidth,
  orientation,
  highlightedSquare,
  highlightColor,
  onSquareClick,
  disabled,
}: NativeFallbackBoardProps) {
  const squareSize = boardWidth / 8;
  const rows =
    orientation === 'white'
      ? [...Array(8)].map((_, i) => 7 - i)
      : [...Array(8)].map((_, i) => i);
  const cols =
    orientation === 'white'
      ? [...Array(8)].map((_, i) => i)
      : [...Array(8)].map((_, i) => 7 - i);

  return (
    <View
      style={[styles.nativeBoard, {width: boardWidth, height: boardWidth}]}>
      {rows.map((rankIdx, rowPos) => (
        <View key={rankIdx} style={styles.nativeRow}>
          {cols.map((fileIdx, colPos) => {
            const square = getSquareName(fileIdx, rankIdx, orientation);
            const isLight = (fileIdx + rankIdx) % 2 === 0;
            const isHighlighted = highlightedSquare === square;
            const isBottomRow = rowPos === 7;
            const isLeftCol = colPos === 0;
            const fileLabel =
              FILES[orientation === 'white' ? fileIdx : 7 - fileIdx];
            const rankLabel =
              RANKS[orientation === 'white' ? 7 - rankIdx : rankIdx];

            return (
              <Pressable
                key={square}
                disabled={disabled}
                onPress={() => onSquareClick(square)}
                style={[
                  styles.nativeSquare,
                  {
                    width: squareSize,
                    height: squareSize,
                    backgroundColor: isHighlighted
                      ? highlightColor
                      : isLight
                      ? LIGHT_SQUARE
                      : DARK_SQUARE,
                  },
                ]}>
                {isLeftCol && (
                  <Text
                    style={[
                      styles.notationLabel,
                      styles.notationRank,
                      {fontSize: Math.max(8, squareSize * 0.22)},
                    ]}>
                    {rankLabel}
                  </Text>
                )}
                {isBottomRow && (
                  <Text
                    style={[
                      styles.notationLabel,
                      styles.notationFile,
                      {fontSize: Math.max(8, squareSize * 0.22)},
                    ]}>
                    {fileLabel}
                  </Text>
                )}
              </Pressable>
            );
          })}
        </View>
      ))}
    </View>
  );
}

export interface FunChessboardProps {
  boardWidth: number;
  orientation: 'white' | 'black';
  highlightedSquare: string | null;
  highlightColor: string;
  onSquareClick: (square: string) => void;
  disabled?: boolean;
  /**
   * When true (default), the board renders WITHOUT pieces — Square Trainer's
   * "click the square" UX. PTB sets this to false so the puzzle position is
   * visible.
   */
  hidePieces?: boolean;
  /**
   * Standard FEN string (or "start") fed to react-chessboard's `position`
   * prop. PTB advances this as the player + opponent move alternately.
   * Default: "start".
   */
  position?: string;
  /**
   * Optional last-move from/to squares. When set, both squares get a faint
   * blue overlay so the player has context for the previous move (PTB
   * renders the opponent's setup move; future enhancement: animate moves).
   */
  lastMove?: {from: string; to: string} | null;
  /**
   * Pass-through to react-chessboard. When false (default), pieces are not
   * draggable — PTB drives moves through onPieceDrop OR onSquareClick chain.
   */
  arePiecesDraggable?: boolean;
  /**
   * Pass-through to react-chessboard. Called when the user drags a piece.
   * PTB uses this to walk the line — return true to accept, false to reject.
   */
  onPieceDrop?: (from: string, to: string, piece: string) => boolean;
}

/** All 12 piece codes mapped to a hidden zero-size element so the board renders empty. */
function createEmptyPieces(): Record<string, () => React.ReactElement> {
  const pieces = [
    'wP', 'wR', 'wN', 'wB', 'wQ', 'wK',
    'bP', 'bR', 'bN', 'bB', 'bQ', 'bK',
  ];
  const empty = () =>
    React.createElement('div', {
      style: {
        width: 0,
        height: 0,
        overflow: 'hidden',
        position: 'absolute',
      },
    });
  const map: Record<string, () => React.ReactElement> = {};
  pieces.forEach(p => {
    map[p] = empty;
  });
  return map;
}

// Faint blue overlay used on the from/to squares of the last move.
const LAST_MOVE_HIGHLIGHT = 'rgba(56, 132, 232, 0.28)';

export default function FunChessboard({
  boardWidth,
  orientation,
  highlightedSquare,
  highlightColor,
  onSquareClick,
  disabled = false,
  hidePieces = true,
  position = 'start',
  lastMove = null,
  arePiecesDraggable = false,
  onPieceDrop,
}: FunChessboardProps) {
  // Only build the empty-pieces map when we actually need to hide them; PTB
  // wants real pieces so we let react-chessboard render its default set.
  const customPieces = useMemo(
    () => (hidePieces ? createEmptyPieces() : undefined),
    [hidePieces],
  );

  const customSquareStyles = useMemo(() => {
    const out: Record<string, Record<string, string | number>> = {};
    if (lastMove && lastMove.from) {
      out[lastMove.from] = {backgroundColor: LAST_MOVE_HIGHLIGHT};
    }
    if (lastMove && lastMove.to) {
      out[lastMove.to] = {backgroundColor: LAST_MOVE_HIGHLIGHT};
    }
    // Click highlight wins over last-move so correct/wrong feedback is
    // always visible.
    if (highlightedSquare != null) {
      out[highlightedSquare] = {backgroundColor: highlightColor};
    }
    return out;
  }, [highlightedSquare, highlightColor, lastMove]);

  if (!boardWidth || boardWidth <= 0) {
    return (
      <View style={[styles.fallback, {width: 200, height: 200}]}>
        <View style={styles.fallbackInner} />
      </View>
    );
  }

  // On native, react-chessboard is DOM-only — fall back to the grid.
  if (Platform.OS !== 'web') {
    return (
      <View
        style={[styles.wrapper, {width: boardWidth, height: boardWidth}]}>
        <NativeFallbackBoard
          boardWidth={boardWidth}
          orientation={orientation}
          highlightedSquare={highlightedSquare}
          highlightColor={highlightColor}
          onSquareClick={onSquareClick}
          disabled={disabled}
        />
      </View>
    );
  }

  // Web: react-chessboard.
  let Chessboard: React.ComponentType<any>;
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    Chessboard = require('react-chessboard').Chessboard;
  } catch {
    return (
      <View
        style={[styles.wrapper, {width: boardWidth, height: boardWidth}]}>
        <NativeFallbackBoard
          boardWidth={boardWidth}
          orientation={orientation}
          highlightedSquare={highlightedSquare}
          highlightColor={highlightColor}
          onSquareClick={onSquareClick}
          disabled={disabled}
        />
      </View>
    );
  }

  return (
    <View
      style={[
        styles.wrapper,
        {
          width: boardWidth,
          height: boardWidth,
          minWidth: boardWidth,
          minHeight: boardWidth,
        },
      ]}>
      <Chessboard
        position={position}
        boardOrientation={orientation}
        boardWidth={boardWidth}
        customLightSquareStyle={{backgroundColor: LIGHT_SQUARE}}
        customDarkSquareStyle={{backgroundColor: DARK_SQUARE}}
        customSquareStyles={customSquareStyles}
        {...(customPieces ? {customPieces} : {})}
        onSquareClick={(square: string) =>
          !disabled && onSquareClick(square)
        }
        arePiecesDraggable={arePiecesDraggable && !disabled}
        {...(onPieceDrop
          ? {
              onPieceDrop: (from: string, to: string, piece: string) =>
                !disabled && onPieceDrop(from, to, piece),
            }
          : {})}
        areArrowsAllowed={false}
        showBoardNotation={!hidePieces}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: '#262322',
  },
  fallback: {
    backgroundColor: '#F3EFE7',
    justifyContent: 'center',
    alignItems: 'center',
  },
  fallbackInner: {
    width: 100,
    height: 100,
  },
  nativeBoard: {
    borderWidth: 2,
    borderColor: '#262322',
    overflow: 'hidden',
  },
  nativeRow: {
    flexDirection: 'row',
  },
  nativeSquare: {
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  notationLabel: {
    position: 'absolute',
    fontWeight: '700',
    color: NOTATION_COLOR,
  },
  notationRank: {
    top: 2,
    left: 2,
  },
  notationFile: {
    bottom: 2,
    right: 2,
  },
});
